import { useMemo, useState } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { DollarSign, TrendingDown, TrendingUp, AlertTriangle } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/shared/ErrorState'
import {
  useAIUsage,
  type AIUsageResponse,
  type AIUsageWindow,
} from '@/hooks/useAIUsage'
import { useQuotaHealth, type QuotaStatus } from '@/hooks/useQuotaHealth'
import { useAICostConfig, useSaveAICostConfig } from '@/hooks/useAICostConfig'

// ─── Formatting helpers ───────────────────────────────────────────────────────

function formatCost(value: number): string {
  return `$${value.toFixed(2)}`
}

function formatCostPrecise(value: number): string {
  return `$${value.toFixed(4)}`
}

function formatDay(iso: string): string {
  const [, m, d] = iso.split('-').map((s) => parseInt(s, 10))
  if (!m || !d) return iso
  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ]
  return `${MONTHS[m - 1]} ${d}`
}

function pctColor(pct: number | undefined): string {
  if (pct === undefined) return ''
  if (pct >= 90) return 'text-destructive'
  if (pct >= 70) return 'text-amber-500'
  return 'text-emerald-500'
}

function statusBadge(status: QuotaStatus): string {
  if (status === 'ok') return 'bg-emerald-500/10 text-emerald-600 border border-emerald-500/30'
  if (status === 'warn') return 'bg-amber-500/10 text-amber-600 border border-amber-500/30'
  if (status === 'critical') return 'bg-destructive/10 text-destructive border border-destructive/30'
  return 'bg-muted text-muted-foreground border border-border'
}

// ─── KPI stat card ────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  )
}

// ─── Cost trend sparkline (full-width, above the 3 panels) ───────────────────

function CostTrendChart({
  daily,
  windowDays,
}: {
  daily: AIUsageResponse['daily']
  windowDays: number
}) {
  const data = useMemo(
    () => daily.map((d) => ({ day: formatDay(d.day), cost: d.cost })),
    [daily],
  )
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cost over last {windowDays} days</CardTitle>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No AI calls in this window.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={data} margin={{ top: 4, right: 12, bottom: 0, left: 0 }}>
              <defs>
                <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563eb" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid-stroke)" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 11, fill: 'var(--chart-axis-label)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                tick={{ fontSize: 11, fill: 'var(--chart-axis-label)' }}
                axisLine={false}
                tickLine={false}
                width={52}
              />
              <Tooltip
                formatter={(v: number) => [`$${v.toFixed(2)}`, 'Cost']}
                contentStyle={{
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border-default)',
                  borderRadius: '0.5rem',
                  fontSize: '0.75rem',
                }}
              />
              <Area
                type="monotone"
                dataKey="cost"
                stroke="#2563eb"
                strokeWidth={2}
                fill="url(#costGradient)"
                dot={{ r: 3, fill: '#2563eb' }}
                activeDot={{ r: 5 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Panel 1: Task Costs ──────────────────────────────────────────────────────

function TaskCostsPanel({
  breakdown,
  totalCost,
}: {
  breakdown: AIUsageResponse['breakdown']
  totalCost: number
}) {
  const byTask = useMemo(() => {
    const map: Record<string, { cost: number; calls: number; rateLimited: number }> = {}
    for (const r of breakdown) {
      if (!map[r.task]) map[r.task] = { cost: 0, calls: 0, rateLimited: 0 }
      map[r.task].cost += r.cost
      map[r.task].calls += r.requests
      map[r.task].rateLimited += r.rateLimitedCount
    }
    return Object.entries(map).sort((a, b) => b[1].cost - a[1].cost)
  }, [breakdown])

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Task Costs</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 p-0">
        {byTask.length === 0 ? (
          <p className="text-sm text-muted-foreground px-4 pb-4">No data in window.</p>
        ) : (
          <div className="divide-y text-sm">
            {byTask.map(([task, { cost, calls }]) => {
              const pct = totalCost > 0 ? (cost / totalCost) * 100 : 0
              return (
                <div key={task} className="px-4 py-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="capitalize font-medium">{task}</span>
                    <span className="tabular-nums text-xs">{formatCost(cost)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-muted rounded-full h-1.5">
                      <div
                        className="bg-primary rounded-full h-1.5"
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground tabular-nums w-10 text-right">
                      {pct.toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {calls.toLocaleString('en-US')} calls
                  </p>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Panel 2: Provider Health ─────────────────────────────────────────────────

function ProviderHealthPanel({
  breakdown,
}: {
  breakdown: AIUsageResponse['breakdown']
}) {
  const byProvider = useMemo(() => {
    const map: Record<
      string,
      { name: string; cost: number; calls: number; rateLimited: number }
    > = {}
    for (const r of breakdown) {
      const key = `${r.providerName}::${r.model}`
      if (!map[key])
        map[key] = { name: r.providerName, cost: 0, calls: 0, rateLimited: 0 }
      map[key].cost += r.cost
      map[key].calls += r.requests
      map[key].rateLimited += r.rateLimitedCount
    }
    return Object.values(map).sort((a, b) => b.cost - a.cost)
  }, [breakdown])

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Provider Health</CardTitle>
      </CardHeader>
      <CardContent className="flex-1 p-0">
        {byProvider.length === 0 ? (
          <p className="text-sm text-muted-foreground px-4 pb-4">No data in window.</p>
        ) : (
          <div className="divide-y text-sm">
            {byProvider.map(({ name, cost, calls, rateLimited }, i) => (
              <div key={i} className="px-4 py-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium capitalize">{name}</span>
                  <span className="tabular-nums text-xs">{formatCost(cost)}</span>
                </div>
                <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                  <span>{calls.toLocaleString('en-US')} calls</span>
                  <span className={rateLimited > 0 ? 'text-amber-500 font-medium' : ''}>
                    {rateLimited > 0 ? (
                      <>
                        <AlertTriangle className="inline h-3 w-3 mr-0.5" />
                        {rateLimited} rate-limited
                      </>
                    ) : (
                      '0 rate-limited'
                    )}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Panel 3: Quota Status ────────────────────────────────────────────────────

function QuotaBar({
  label,
  used,
  limit,
  pct,
  status,
}: {
  label: string
  used?: number
  limit?: number
  pct?: number
  status: QuotaStatus
}) {
  const colour =
    status === 'critical'
      ? 'bg-destructive'
      : status === 'warn'
        ? 'bg-amber-500'
        : status === 'ok'
          ? 'bg-emerald-500'
          : 'bg-muted-foreground/40'
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1 text-xs">
        <span className="font-medium">{label}</span>
        {pct !== undefined && used !== undefined && limit !== undefined ? (
          <span className={`tabular-nums ${pctColor(pct)}`}>
            {used} / {limit} ({pct}%)
          </span>
        ) : (
          <span className={`px-1.5 py-0.5 rounded text-xs ${statusBadge(status)}`}>
            {status}
          </span>
        )}
      </div>
      <div className="w-full bg-muted rounded-full h-2">
        <div
          className={`${colour} rounded-full h-2 transition-all`}
          style={{ width: `${Math.min(pct ?? 0, 100)}%` }}
        />
      </div>
    </div>
  )
}

function MonthlyCapForm() {
  const { data: config, isLoading } = useAICostConfig()
  const { mutate: saveCap, isPending, isSuccess, isError } = useSaveAICostConfig()
  const [capInput, setCapInput] = useState<string>('')

  const serverCap = config?.monthly_cap_usd ?? 0
  const displayValue =
    capInput !== '' ? capInput : String(serverCap === 0 ? '' : serverCap)

  function handleSave() {
    const value = parseFloat(capInput)
    if (!Number.isFinite(value) || value < 0) return
    saveCap(value)
    setCapInput('')
  }

  return (
    <div className="mt-4 pt-4 border-t">
      <p className="text-xs font-medium mb-2">Monthly budget cap (USD)</p>
      <div className="flex items-center gap-2">
        <div className="relative w-32">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
            $
          </span>
          <Input
            type="number"
            min="0"
            step="1"
            value={displayValue}
            onChange={(e) => setCapInput(e.target.value)}
            className="pl-6"
            placeholder={isLoading ? '…' : '0'}
            aria-label="Monthly budget cap in USD"
          />
        </div>
        <Button onClick={handleSave} size="sm" disabled={isPending}>
          Save
        </Button>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        0 = disabled. Emitter checks cap on each 30-min tick.
      </p>
      {isSuccess && <p className="text-xs text-emerald-600 mt-1">Saved.</p>}
      {isError && (
        <p className="text-xs text-destructive mt-1">Save failed — try again.</p>
      )}
    </div>
  )
}

function QuotaStatusPanel() {
  const { data: quota, isLoading } = useQuotaHealth()

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">Quota Status</CardTitle>
      </CardHeader>
      <CardContent className="flex-1">
        {isLoading ? (
          <>
            <Skeleton className="h-4 w-full mb-3" />
            <Skeleton className="h-4 w-3/4" />
          </>
        ) : quota ? (
          <>
            <QuotaBar
              label="Sentry errors (30d / 5k)"
              used={quota.sentry.used}
              limit={quota.sentry.limit}
              pct={quota.sentry.pct}
              status={quota.sentry.status}
            />
            <QuotaBar
              label="Grafana logs GB (30d / 50 GB)"
              used={quota.grafana_logs_gb.used}
              limit={quota.grafana_logs_gb.limit}
              pct={quota.grafana_logs_gb.pct}
              status={quota.grafana_logs_gb.status}
            />
          </>
        ) : (
          <p className="text-xs text-muted-foreground">Quota data unavailable.</p>
        )}
        <MonthlyCapForm />
      </CardContent>
    </Card>
  )
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div
      className="flex flex-col gap-6"
      aria-busy="true"
      aria-label="Loading AI cost data"
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <Skeleton className="h-8 w-20 mb-2" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      <Skeleton className="h-48 w-full rounded-lg" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-64 rounded-lg" />
        ))}
      </div>
    </div>
  )
}

// ─── Window selector ──────────────────────────────────────────────────────────

const WINDOW_OPTIONS: Array<{ value: AIUsageWindow; label: string }> = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
]

function WindowSelect({
  value,
  onChange,
}: {
  value: AIUsageWindow
  onChange: (next: AIUsageWindow) => void
}) {
  return (
    <div className="flex items-center gap-2">
      <label htmlFor="ai-cost-window" className="text-sm text-muted-foreground">
        Window
      </label>
      <select
        id="ai-cost-window"
        data-testid="ai-cost-window-select"
        value={String(value)}
        onChange={(e) => onChange(parseInt(e.target.value, 10) as AIUsageWindow)}
        className="h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {WINDOW_OPTIONS.map((opt) => (
          <option key={opt.value} value={String(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AICostDashboardPage() {
  const [window, setWindow] = useState<AIUsageWindow>(7)
  const { data, isLoading, isError, refetch } = useAIUsage({ days: window })

  const momArrow =
    data?.totals.momDelta != null ? (
      data.totals.momDelta >= 0 ? (
        <TrendingUp className="inline h-3 w-3 text-destructive mr-0.5" />
      ) : (
        <TrendingDown className="inline h-3 w-3 text-emerald-500 mr-0.5" />
      )
    ) : null

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <DollarSign className="h-6 w-6 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">AI Cost &amp; Usage</h1>
            <p className="text-muted-foreground text-sm">
              Monitor AI spending and free-tier quota burn
            </p>
          </div>
        </div>
        <WindowSelect value={window} onChange={setWindow} />
      </div>

      {isLoading && <LoadingSkeleton />}

      {isError && (
        <ErrorState
          title="Couldn't load AI usage"
          message="The admin usage endpoint returned an error. Check the API logs and retry."
          onRetry={() => refetch()}
        />
      )}

      {data && !isLoading && !isError && (
        <>
          {/* KPI rail */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label={`Total spend (${data.window.days}d)`}
              value={formatCost(data.totals.cost)}
              sub={<>{data.totals.requests.toLocaleString('en-US')} AI calls</>}
            />
            <StatCard
              label="Cost per article"
              value={formatCostPrecise(data.totals.costPerArticle)}
              sub="Distinct articles in window"
            />
            <StatCard
              label="Success rate"
              value={`${(data.totals.successRate * 100).toFixed(1)}%`}
              sub={<>{data.totals.rateLimitedCount} rate-limited</>}
            />
            <StatCard
              label="vs prior period"
              value={
                data.totals.momDelta != null
                  ? `${data.totals.momDelta > 0 ? '+' : ''}${data.totals.momDelta}%`
                  : '—'
              }
              sub={
                <>
                  {momArrow}
                  {data.totals.momDelta != null ? 'vs prior window' : 'no prior data'}
                </>
              }
            />
          </div>

          {/* Trend chart */}
          <CostTrendChart daily={data.daily} windowDays={data.window.days} />

          {/* Three panels */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <TaskCostsPanel breakdown={data.breakdown} totalCost={data.totals.cost} />
            <ProviderHealthPanel breakdown={data.breakdown} />
            <QuotaStatusPanel />
          </div>
        </>
      )}
    </div>
  )
}
